import React, { useState } from 'react';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import SortIcon from 'assets/sort.svg?react';
import LayoutStrings from '../../../../../../Constants/layoutStrings';

// Checkmark icon component
const CheckmarkIcon: React.FC<{ visible: boolean }> = ({ visible }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    style={{ visibility: visible ? 'visible' : 'hidden' }}
  >
    <path
      d="M8.79508 15.8769L4.62508 11.7069L3.20508 13.1169L8.79508 18.7069L20.7951 6.70687L19.3851 5.29688L8.79508 15.8769Z"
      fill="#0071CD"
    />
  </svg>
);

// Sort options
export const SORT_TYPES = {
  NAME_ASC: 'name-asc',
  NAME_DESC: 'name-desc',
  COLOR: 'color'
} as const;

export type SortTypeValue = typeof SORT_TYPES[keyof typeof SORT_TYPES];

interface SortDropdownProps {
  currentSort: SortTypeValue;
  onSortChange: (sortType: SortTypeValue) => void;
  disabled?: boolean;
}

const SortDropdown: React.FC<SortDropdownProps> = ({
  currentSort,
  onSortChange,
  disabled = false
}) => {
  const [isSortOpen, setIsSortOpen] = useState(false);

  const handleSort = () => {
    if (!disabled) {
      setIsSortOpen(!isSortOpen);
    }
  };

  const handleSortOptionClick = (sortType: SortTypeValue) => {
    onSortChange(sortType);
    setIsSortOpen(false);
  };

  return (
    <div className="relative">
      <Button
        size="middle"
        type={ButtonType.outline}
        onClick={handleSort}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isSortOpen}
      >
        <div className="flex items-center gap-1">
          <SortIcon width="18" height="18" />
          <span>{LayoutStrings.buttons.sort}</span>
        </div>
      </Button>

      {isSortOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <button
            type="button"
            className="fixed inset-0 z-10 bg-transparent border-0 p-0 cursor-default"
            tabIndex={-1}
            onClick={() => setIsSortOpen(false)}
            onKeyDown={e => {
              if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsSortOpen(false);
              }
            }}
            aria-label="Close dropdown"
          />

          {/* Dropdown menu */}
          <div
            className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-md shadow-lg flex flex-col"
            style={{ width: '172px', height: '104px' }}
          >
            <button
              type="button"
              className={`flex-1 text-left px-3 py-1 text-sm transition-colors rounded-t-md ${
                currentSort === SORT_TYPES.NAME_ASC
                  ? 'bg-selectedBlue font-semibold'
                  : 'text-gray-700'
              }`}
              onClick={() => handleSortOptionClick(SORT_TYPES.NAME_ASC)}
              style={{
                ...(currentSort === SORT_TYPES.NAME_ASC
                  ? { color: '#14151C' }
                  : {}),
                ...(currentSort === SORT_TYPES.NAME_ASC
                  ? undefined
                  : {
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    })
              }}
              onMouseEnter={e => {
                if (currentSort !== SORT_TYPES.NAME_ASC) {
                  e.currentTarget.style.background = '#F3F4F6';
                }
              }}
              onMouseLeave={e => {
                if (currentSort !== SORT_TYPES.NAME_ASC) {
                  e.currentTarget.style.background = '';
                }
              }}
            >
              <div className="flex items-center h-full">
                <div className="mr-2" style={{ width: '24px', height: '24px' }}>
                  <CheckmarkIcon
                    visible={currentSort === SORT_TYPES.NAME_ASC}
                  />
                </div>
                <span>{LayoutStrings.sortOptions.nameAsc}</span>
              </div>
            </button>
            <button
              type="button"
              className={`flex-1 text-left px-3 py-1 text-sm transition-colors ${
                currentSort === SORT_TYPES.NAME_DESC
                  ? 'bg-selectedBlue font-semibold'
                  : 'text-gray-700'
              }`}
              onClick={() => handleSortOptionClick(SORT_TYPES.NAME_DESC)}
              style={{
                ...(currentSort === SORT_TYPES.NAME_DESC
                  ? { color: '#14151C' }
                  : {}),
                ...(currentSort === SORT_TYPES.NAME_DESC
                  ? undefined
                  : {
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    })
              }}
              onMouseEnter={e => {
                if (currentSort !== SORT_TYPES.NAME_DESC) {
                  e.currentTarget.style.background = '#F3F4F6';
                }
              }}
              onMouseLeave={e => {
                if (currentSort !== SORT_TYPES.NAME_DESC) {
                  e.currentTarget.style.background = '';
                }
              }}
            >
              <div className="flex items-center h-full">
                <div className="mr-2" style={{ width: '24px', height: '24px' }}>
                  <CheckmarkIcon
                    visible={currentSort === SORT_TYPES.NAME_DESC}
                  />
                </div>
                <span>{LayoutStrings.sortOptions.nameDesc}</span>
              </div>
            </button>
            <button
              type="button"
              className={`flex-1 text-left px-3 py-1 text-sm transition-colors rounded-b-md ${
                currentSort === SORT_TYPES.COLOR
                  ? 'bg-selectedBlue font-semibold'
                  : 'text-gray-700'
              }`}
              onClick={() => handleSortOptionClick(SORT_TYPES.COLOR)}
              style={{
                ...(currentSort === SORT_TYPES.COLOR
                  ? { color: '#14151C' }
                  : {}),
                ...(currentSort === SORT_TYPES.COLOR
                  ? undefined
                  : {
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    })
              }}
              onMouseEnter={e => {
                if (currentSort !== SORT_TYPES.COLOR) {
                  e.currentTarget.style.background = '#F3F4F6';
                }
              }}
              onMouseLeave={e => {
                if (currentSort !== SORT_TYPES.COLOR) {
                  e.currentTarget.style.background = '';
                }
              }}
            >
              <div className="flex items-center h-full">
                <div className="mr-2" style={{ width: '24px', height: '24px' }}>
                  <CheckmarkIcon visible={currentSort === SORT_TYPES.COLOR} />
                </div>
                <span>{LayoutStrings.sortOptions.color}</span>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SortDropdown;
